import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { IconMicrophone, IconPlayerStopFilled, IconX } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { MAX_RECORDING_SECONDS, useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { useChatArtifactStore } from '../../lib/stores/ChatArtifactStore';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { bugReportSchema } from './bugReportSchema';
import { useBugReport } from './useBugReport';
const clock = (seconds)=>`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
export function BugReportModal({ isOpen, onOpenChange, screenshot = null, onRemoveScreenshot }) {
    const { user } = useAuth();
    const { submitBugReport, draftBugReport, isLoading } = useBugReport();
    const [voice, setVoice] = useState(null);
    const [isDrafting, setIsDrafting] = useState(false);
    const [draftError, setDraftError] = useState(null);
    const screenshotUrl = useMemo(()=>screenshot ? URL.createObjectURL(screenshot) : null, [
        screenshot
    ]);
    useEffect(()=>()=>void (screenshotUrl && URL.revokeObjectURL(screenshotUrl)), [
        screenshotUrl
    ]);
    const chatArtifactState = useChatArtifactStore((state)=>({
            currentChatId: state.currentChatId,
            artifacts: state.getArtifacts()
        }));
    const layoutState = useLayoutStore((state)=>({
            pageHeaderContent: state.pageHeaderContent,
            showBreadcrumbs: state.showBreadcrumbs
        }));
    const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } = useForm({
        resolver: zodResolver(bugReportSchema),
        defaultValues: {
            title: '',
            description: '',
            includeScreenshot: false
        }
    });
    // Bumped on close. A draft still in flight when the dialog closes belongs to
    // the report that was abandoned, and must not fill in the next one.
    const draftGeneration = useRef(0);
    const recorder = useVoiceRecorder(async (recording)=>{
        const generation = draftGeneration.current;
        const isCurrent = ()=>generation === draftGeneration.current;
        setIsDrafting(true);
        setDraftError(null);
        try {
            const draft = await draftBugReport(recording);
            if (!isCurrent()) return;
            setVoice({
                ...recording,
                transcript: draft.transcript
            });
            // A blank title means the rewrite failed and the transcript came back
            // as the description; leave whatever title they already typed alone.
            if (draft.title) setValue('title', draft.title, {
                shouldValidate: true
            });
            setValue('description', draft.description, {
                shouldValidate: true
            });
        } catch (error) {
            if (isCurrent()) {
                setDraftError(apiErrorMessage(error, 'Could not transcribe that. Try again or type it.'));
            }
        } finally{
            if (isCurrent()) setIsDrafting(false);
        }
    });
    // Every open is a fresh report: the button captures a new screenshot each
    // time, so a recording or draft left over from the last one would be filed
    // against the wrong page. Keyed on isOpen rather than on each close path so
    // the overlay, Escape, Cancel and a successful submit all get it.
    const { cancel: cancelRecording } = recorder;
    useEffect(()=>{
        if (isOpen) return;
        draftGeneration.current++;
        cancelRecording();
        reset();
        setVoice(null);
        setDraftError(null);
        setIsDrafting(false);
    }, [
        isOpen,
        cancelRecording,
        reset
    ]);
    const voiceStatus = recorder.isRecording ? `Recording ${clock(recorder.elapsed)} / ${clock(MAX_RECORDING_SECONDS)}. Tap to stop.` : isDrafting ? 'Transcribing…' : recorder.error ?? draftError ?? (voice ? 'Drafted from your recording. Fix anything that is wrong.' : 'Tap the mic and tell us what went wrong.');
    const getAppStateJson = ()=>{
        try {
            return JSON.stringify({
                chatArtifacts: {
                    currentChatId: chatArtifactState.currentChatId,
                    artifactCount: chatArtifactState.artifacts.length,
                    artifacts: chatArtifactState.artifacts.map((a)=>({
                            id: a.id,
                            kind: a.kind,
                            title: a.title,
                            createdAt: a.createdAt,
                            updatedAt: a.updatedAt
                        }))
                },
                layout: layoutState
            }, null, 2);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            console.error('Failed to serialize app state:', message);
            // Return minimal state on error
            return JSON.stringify({
                error: 'Failed to capture app state',
                message
            }, null, 2);
        }
    };
    const onSubmit = async (data)=>{
        try {
            if (!user) {
                toast.error('You must be logged in to report a bug');
                return;
            }
            const appStateJson = getAppStateJson();
            const result = await submitBugReport({
                title: data.title,
                description: data.description,
                screenshot,
                audio: voice?.audio,
                audioFilename: voice?.filename,
                transcript: voice?.transcript,
                pageUrl: window.location.href,
                userInfo: {
                    id: user.id,
                    name: user.name,
                    email: user.email
                },
                browserInfo: getBrowserInfo(),
                timestamp: new Date().toISOString(),
                appState: appStateJson
            });
            toast.success(`Bug reported! Ticket: ${result.ticketNumber}`, {
                description: 'You can view all your bug reports in Settings > Bug Reports'
            });
            onOpenChange(false);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to submit bug report'));
        }
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: isOpen,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "sm:max-w-[600px] max-h-[90vh] overflow-y-auto",
            "data-testid": Testids.bugReport.dialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Report a Bug"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Tell us what went wrong, out loud or typed. App state is captured automatically."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit(onSubmit),
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: recorder.isRecording ? recorder.stop : recorder.start,
                                    disabled: isDrafting,
                                    "data-testid": Testids.bugReport.recordButton,
                                    "aria-label": recorder.isRecording ? 'Stop recording' : 'Record a voice report',
                                    className: recorder.isRecording ? 'flex size-12 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground animate-pulse' : 'flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50',
                                    children: recorder.isRecording ? /*#__PURE__*/ _jsx(IconPlayerStopFilled, {
                                        size: 20
                                    }) : /*#__PURE__*/ _jsx(IconMicrophone, {
                                        size: 20
                                    })
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    "data-testid": Testids.bugReport.voiceStatus,
                                    "aria-live": "polite",
                                    children: voiceStatus
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "text-sm font-medium",
                                    children: [
                                        "Title ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-destructive",
                                            children: "*"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    ...register('title'),
                                    "data-testid": Testids.bugReport.titleInput,
                                    type: "text",
                                    placeholder: "Brief summary of the issue",
                                    className: "w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                }),
                                errors.title && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-destructive",
                                    children: errors.title.message
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "text-sm font-medium",
                                    children: [
                                        "Description ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-destructive",
                                            children: "*"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("textarea", {
                                    ...register('description'),
                                    "data-testid": Testids.bugReport.descriptionInput,
                                    placeholder: "Detailed description of the issue",
                                    rows: 4,
                                    className: "w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                }),
                                errors.description && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-destructive",
                                    children: errors.description.message
                                })
                            ]
                        }),
                        screenshotUrl && /*#__PURE__*/ _jsxs("div", {
                            className: "relative w-fit",
                            "data-testid": Testids.bugReport.screenshotPreview,
                            children: [
                                /*#__PURE__*/ _jsx("img", {
                                    src: screenshotUrl,
                                    alt: "Screenshot attached to this report",
                                    className: "max-h-40 rounded-md border border-input"
                                }),
                                onRemoveScreenshot && /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: onRemoveScreenshot,
                                    "aria-label": "Remove screenshot",
                                    className: "absolute right-1 top-1 rounded-full bg-background/90 p-1 text-muted-foreground hover:text-foreground",
                                    children: /*#__PURE__*/ _jsx(IconX, {
                                        size: 14
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "bg-muted p-3 rounded-md space-y-1 text-xs text-muted-foreground",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: "Page:"
                                        }),
                                        " ",
                                        window.location.pathname
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: "User:"
                                        }),
                                        " ",
                                        user?.name,
                                        " (",
                                        user?.email,
                                        ")"
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: "Timestamp:"
                                        }),
                                        " ",
                                        new Date().toLocaleString()
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: "App State:"
                                        }),
                                        " Included in report"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DialogFooter, {
                            className: "gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    variant: "secondary",
                                    onClick: ()=>onOpenChange(false),
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "submit",
                                    disabled: isSubmitting || isLoading || recorder.isRecording || isDrafting,
                                    "data-testid": Testids.bugReport.submitButton,
                                    children: isSubmitting || isLoading ? 'Submitting...' : 'Submit Report'
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
// Helper function to get browser info
function getBrowserInfo() {
    const ua = navigator.userAgent;
    const browserName = ua.includes('Firefox') ? 'Firefox' : ua.includes('Chrome') ? 'Chrome' : ua.includes('Safari') ? 'Safari' : 'Unknown';
    const os = ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'macOS' : 'Unknown';
    return `${browserName} on ${os}`;
}
